import {
    ACCOUNT_ID_FIELDS, ApplicationResubmitReason, ApplicationResubmitRequestPayload, ApplicationStatus, MIN_ROOMS_FOR_NOMINATOR,
    MIN_ROOMS_FOR_VOTER, getAccountIdFieldInfo, getTrackerSiteLabels
} from '../models/user.model';

export function escapeHtml(text: string): string {
  return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
}

export function escapeHtmlLines(text: string): string {
  return escapeHtml(text).trim().replace(/(?:\r?\n)/g, '<br>');
}

export function getResubmitEmailSubject(projectTitle: string, isUpgrade: boolean): string {
  if (isUpgrade) {
    return `Your ${projectTitle} upgrade request has been processed`;
  }
  return `Your ${projectTitle} application requires more information`;
}

export function buildResubmitHtmlBody(payload: ApplicationResubmitRequestPayload, isUpgrade: boolean): string {
  const intro = isUpgrade ? `
<p>
  Thanks for requesting to upgrade your TERPECA account status from voter to nominator.
</p>` : `
<p>
  Thank you for submitting your application to join the TERPECA team in trying to find
  the world's best escape rooms.
</p>`;
  return `${intro}
${buildResubmitReasonsHtml(payload)}`;
}

export function wrapUserStatusEmailInnerHtml(userName: string, htmlBody: string): string {
  return `
<p>
  Hi ${escapeHtml(userName)}!
</p>
  ${htmlBody}
<p>
  If you have any questions about this process or just to learn more, please visit the
  <a href="https://voters.terpeca.com/faq">TERPECA Voter Portal FAQ</a>, and
  if you still have questions, please feel free to reply here and let us know.
</p>
<p>
  Happy escaping!
</p>
<p>
  The Top Escape Rooms Project team
</p>`;
}

export function wrapUserStatusEmailHtml(userName: string, htmlBody: string): string {
  return `
<html><head></head><body>
${wrapUserStatusEmailInnerHtml(userName, htmlBody)}
</body></html>`;
}

export function buildResubmitEmailHtml(
    payload: ApplicationResubmitRequestPayload, options: {
      userName: string;
      isUpgrade: boolean;
      innerOnly?: boolean;
    }): string {
  const inner = wrapUserStatusEmailInnerHtml(
      options.userName, buildResubmitHtmlBody(payload, options.isUpgrade));
  if (options.innerOnly) {
    return inner;
  }
  return `
<html><head></head><body>
${inner}
</body></html>`;
}

export function buildResubmitReasonsHtml(payload: ApplicationResubmitRequestPayload): string {
  let htmlStr = `
<p>
  There were some issues with your application that we need you to fix. Please return to the
  <a href="https://voters.terpeca.com/">TERPECA Voter Portal</a> to address the items below
  before resubmitting.
</p>`;
  const reasons = payload?.reasons || [];
  if (!reasons.length) {
    return htmlStr;
  }
  const hasInvalidIds = reasons.includes(ApplicationResubmitReason.INVALID_IDS);
  for (const reason of reasons) {
    if (reason === ApplicationResubmitReason.INVALID_FACEBOOK && hasInvalidIds) {
      continue;
    }
    const itemHtml = resubmitReasonItemHtml(reason, payload);
    if (!itemHtml) {
      continue;
    }
    htmlStr += `
<p>
  &bull; ${itemHtml}
</p>`;
  }
  return htmlStr;
}

function resubmitReasonItemHtml(
    reason: ApplicationResubmitReason, payload: ApplicationResubmitRequestPayload): string {
  switch (reason) {
    case ApplicationResubmitReason.ONE_PERSON_ONLY:
      return `
  TERPECA applications are for one person. If you applied as a couple, a business, or a group,
  each person needs to apply separately.`;
    case ApplicationResubmitReason.INVALID_NAME:
      return `
  Your name must be capitalized as it will appear on the website (not all lowercase or ALL CAPS),
  and you must use your real first and last name. Aliases are only allowed if you have a well-known
  online presence under that name in the escape room community; if you want to use one, reply to this
  email to request permission before resubmitting.<br>
  &nbsp;&nbsp;&nbsp;&nbsp;Examples:<br>
  &nbsp;&nbsp;&nbsp;&nbsp;John Doe&nbsp;&nbsp;✅<br>
  &nbsp;&nbsp;&nbsp;&nbsp;John DOE&nbsp;&nbsp;❌<br>
  &nbsp;&nbsp;&nbsp;&nbsp;JOHN DOE&nbsp;&nbsp;❌<br>
  &nbsp;&nbsp;&nbsp;&nbsp;J. Doe&nbsp;&nbsp;❌<br>
  &nbsp;&nbsp;&nbsp;&nbsp;John D.&nbsp;&nbsp;❌<br>
  &nbsp;&nbsp;&nbsp;&nbsp;The Lone Ranger&nbsp;&nbsp;❌`;
    case ApplicationResubmitReason.INVALID_CITY:
      return `
  Your city must be capitalized and punctuated as it will appear on the website (not all lowercase
  or ALL CAPS). Make sure the country and state match the city.`;
    case ApplicationResubmitReason.INVALID_FACEBOOK:
      return invalidIdsHtml(['facebookUsername']);
    case ApplicationResubmitReason.INVALID_IDS:
      return invalidIdsHtml(payload.invalidIdFields || []);
    case ApplicationResubmitReason.INSUFFICIENT_BIO:
      return `
  Your bio must be in English, written in complete sentences, and will appear on our public
  website. It should be a short, 1-3 sentence paragraph that identifies who you are in the escape
  room community and/or who you are in the world, not just that you're an enthusiast. Mention a
  company, games you've designed, a blog, or a well-known team if those apply. Any affiliations
  with escape rooms or companies must be included by name in your bio, not just in the affiliations
  section of the application. See examples at
  <a href="https://terpeca.com/">terpeca.com</a> and more guidance in the
  <a href="https://voters.terpeca.com/faq#bio">FAQ</a>. If English isn't your first language,
  write it in your language first, then translate it (for example with Google Translate).`;
    case ApplicationResubmitReason.AFFILIATIONS_NOT_IN_BIO:
      return `
  Any affiliations with escape rooms or companies must be included by name in your bio, not just
  in the affiliations section of the application.`;
    case ApplicationResubmitReason.INSUFFICIENT_IDENTITY_INFO:
      return `
  We need more to connect this application with someone we can find online. Add links to social
  profiles, a website or blog, or forums where you're active. If you have no public online
  presence and no one on the TERPECA staff knows you or your references, we can't accept your
  application yet. Get involved locally and reapply later.`;
    case ApplicationResubmitReason.INSUFFICIENT_GAME_COUNT: {
      const isUpgrade = payload.status === ApplicationStatus.VOTER;
      const levelName = isUpgrade ? 'nominator' : 'voter';
      const minGames = isUpgrade ? MIN_ROOMS_FOR_NOMINATOR : MIN_ROOMS_FOR_VOTER;
      return `
  You reported fewer than ${minGames} in-person games, the minimum to be a ${levelName}. If you
  have already reached ${minGames}, keeping in mind that games you've booked but not yet played
  don't count, update your count and add supporting evidence before resubmitting. Otherwise,
  wait to resubmit until you hit that number.`;
    }
    case ApplicationResubmitReason.INSUFFICIENT_EXPERIENCE_INFO: {
      const trackers = formatList(getTrackerSiteLabels());
      return `
  We need more evidence to support your room count. The most reliable options are a public tracker profile
  (${trackers}) or a Google Spreadsheet of the rooms you've played. Add a link in your application
  or reply here. If you don't keep a list, explain how you got the number. Photo albums, review sites,
  a personal website, or references known to TERPECA staff can also work.`;
    }
    case ApplicationResubmitReason.INSUFFICIENT_CONFLICT_DISCLOSURE:
      return `
  Please disclose any connections to escape rooms or companies that could be a conflict of
  interest in the affiliations section of your application, and also name them in your bio.
  If you have a reason you can't be fully transparent, reply and tell us as much as you can—we'll
  keep it confidential.`;
    case ApplicationResubmitReason.CUSTOM:
      return escapeHtmlLines(payload.customInstructions || '');
  }
}

function invalidIdsHtml(fields: string[]): string {
  const infos = fields
      .map(field => getAccountIdFieldInfo(field))
      .filter((info): info is NonNullable<typeof info> => Boolean(info));
  if (!infos.length) {
    const labels = ACCOUNT_ID_FIELDS.map(info => info.label).join(', ');
    return `
  One or more of the account IDs you provided are not valid. Please check the IDs in your
  application (${labels}) and update any that are wrong.`;
  }
  if (infos.length === 1) {
    const info = infos[0];
    return `
  The ${escapeHtml(info.label)} ID you provided is not valid. Visit your ${escapeHtml(info.label)}
  profile and update it in the application (${escapeHtml(info.idHint)}).`;
  }
  const items = infos.map(info =>
    `<br>&nbsp;&nbsp;&nbsp;&nbsp;${escapeHtml(info.label)}: ${escapeHtml(info.idHint)}`
  ).join('');
  return `
  These account IDs are not valid. Visit each profile and update the ID in your
  application:${items}`;
}

function formatList(items: string[]): string {
  if (items.length <= 1) {
    return items[0] || '';
  }
  if (items.length === 2) {
    return `${items[0]} or ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
}
